import { useEffect, useRef } from "react";

/**
 * Auto-save & auto-restore de formulários públicos em localStorage.
 *
 * Uso:
 *   const [form, setForm] = useState(initial);
 *   const { clear } = useFormDraft("clinic-apply-draft", form, setForm);
 *   // após submit bem-sucedido:
 *   clear();
 *
 * Só grava se ao menos um campo string tiver conteúdo (evita popular todo storage
 * do navegador com formulários vazios). Restaura apenas na primeira montagem.
 */
export function useFormDraft(key, form, setForm) {
  const restored = useRef(false);

  // Restore uma única vez, na montagem
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          setForm((prev) => ({ ...prev, ...saved }));
        }
      }
    } catch { /* ignore corrupt draft */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Save on change (debounced via microtask — barato para formulários pequenos)
  useEffect(() => {
    if (!restored.current) return;
    const hasContent = Object.values(form || {}).some(
      (v) => typeof v === "string" && v.trim().length > 0,
    );
    try {
      if (hasContent) {
        localStorage.setItem(key, JSON.stringify(form));
      } else {
        localStorage.removeItem(key);
      }
    } catch { /* quota exceeded — ignore */ }
  }, [key, form]);

  const clear = () => {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  };

  return { clear };
}
